// THE single resolver for "which backend is this console talking to" — ported from
// tkt-back-office src/lib/backendUrl.js. The REST helpers (lib/api.js) and the better-auth client
// (lib/authClient.js) both read it, so they can never point at different hosts: the session
// cookie lives on the host we authenticated against, and a better-auth call sent anywhere else
// arrives without it and 401s.
//
// Derivation is generic: <anything>.<domain> → backend.<domain>. ops.momoticketing.com talks to
// backend.momoticketing.com — same site, so the session cookie (Domain=.momoticketing.com) rides
// every request. Local and preview hosts have no backend.<domain> sibling and use VITE_BACKEND_URL.
import { BRAND } from './brand.js';

const isLocalOrPreviewHost = (hostname) =>
  hostname === 'localhost' ||
  hostname === '127.0.0.1' ||
  hostname === '0.0.0.0' ||
  /^(\d{1,3}\.){3}\d{1,3}$/.test(hostname) ||
  hostname.endsWith('.local') ||
  hostname.endsWith('.workers.dev');

/** "<sub>.<domain>" for the domain this console is served on, or `fallback` on local/preview hosts. */
export const derivedHostUrl = (sub, fallback) => {
  if (typeof window !== 'undefined' && window.location?.host) {
    const { protocol, host } = window.location;
    const hostname = host.split(':')[0];
    if (!isLocalOrPreviewHost(hostname)) {
      const parts = hostname.split('.');
      const domain = parts.length > 2 ? parts.slice(1).join('.') : hostname;
      return `${protocol}//${sub}.${domain}`;
    }
  }
  return typeof fallback === 'function' ? fallback() : fallback;
};

export const getBackendUrl = () =>
  derivedHostUrl('backend', () => import.meta.env.VITE_BACKEND_URL || 'http://127.0.0.1:3001').replace(/\/$/, '');

export const isLocalBackend = () => /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/i.test(getBackendUrl());

/**
 * THE PDF RENDERER (~/ticketapis): one service for every tenant, the owning backend in its path
 * (tkt-back-office lib/backendUrl getRendererUrl). It fetches a document's public HTML and prints it.
 */
export const getRendererUrl = () => import.meta.env.VITE_TKT_RENDERER_URL || 'https://apis.tkt.ug';

/**
 * A payout's statement as a PDF: the renderer printing the backend's public statement page. A local
 * backend is out of the renderer's reach, so there the page itself opens (it prints from the browser).
 * `at` keeps a cache from answering with an older print.
 */
export const payoutStatementUrl = (payoutId) => {
  const page = `${getBackendUrl()}/api/payouts/${encodeURIComponent(payoutId)}/statement.html`;
  return isLocalBackend() ? page : `${getRendererUrl()}/pdf/${page.replace(/^https?:\/\//, '')}?at=${Date.now()}`;
};

/**
 * THE GATE SCANNER FOR THIS TENANT, as tkt-back-office has it (its lib/backendUrl.js, keep the two in
 * step): ~/ac_scanner, one app built in a flavor per brand. Its web build answers on scanner.<apex>
 * and its Android builds are in one bucket for every brand (scanner-apps/<flavor>-scanner.apk).
 *
 * Keyed by the CONSOLE's apex, which is not always the scanner's: momoticketing.com is the moments
 * backend (same process, measured 2026-10-12), so its scanner is scanner.moments.ug. `scheme` is the
 * app's deep link, for a device code.
 */
const SCANNER_FLAVORS = {
  tkt: { name: 'TKT Scanner', scheme: 'tktscanner' },
  mookh: { name: 'Mookh Scanner', scheme: 'mookhscanner' },
  moments: { name: 'Ticket Scanner', scheme: 'momentsscanner' },
  fanpass: { name: 'Fanpass Scanner', scheme: 'fanpassscanner' },
  tix: { name: 'Tickway Scanner', scheme: 'tixscanner' },
  mandela: { name: 'Mandela Scanner', scheme: 'mandelascanner' },
};
const SCANNER_BY_APEX = {
  'tkt.ug': ['tkt', 'tkt.ug'], 'ticket.ug': ['tkt', 'ticket.ug'],
  'party.ug': ['mookh', 'party.ug'], 'mookh.com': ['mookh', 'party.ug'],
  'moments.ug': ['moments', 'moments.ug'], 'momoticketing.com': ['moments', 'moments.ug'],
  'fanpass.ke': ['fanpass', 'fanpass.ke'], 'tix.ug': ['tix', 'tix.ug'], 'mandelastadium.ug': ['mandela', 'mandelastadium.ug'],
};
const scannerApp = (flavor, apex) => ({
  flavor, ...SCANNER_FLAVORS[flavor] || { name: 'Access Control', scheme: 'acscanner' },
  webUrl: `https://scanner.${apex}`,
  apkUrl: `https://media.ticket.ug/scanner-apps/${flavor}-scanner.apk`,
});

/** This tenant's scanner: name, deep-link scheme, web app and Android download. NULL on a host that names no
 *  tenant, local and preview consoles included: a device code minted on a local backend handed to a production
 *  scanner would send a steward to a server that has never seen it. */
export const getScannerApp = () => {
  const hostname = typeof window !== 'undefined' ? window.location?.hostname || '' : '';
  const hit = !hostname || isLocalOrPreviewHost(hostname) ? null : SCANNER_BY_APEX[hostname.split('.').slice(1).join('.')];
  return hit ? scannerApp(...hit) : null;
};

/**
 * THE BUYER-FACING SITE — where an event's sales page lives: momoticketing.com (mtn-ticketing, route
 * /events/$eventId, which takes the slug or the id). The apex of the domain this console is served
 * on (ops.momoticketing.com → https://momoticketing.com), or momoticketing.com from a local host.
 *
 * Not the backend's `publicUrl`: on moments it says https://momoticket.com/… (2026-10-19), which is
 * not the site MoMo sells on.
 */
export const getStorefrontUrl = () => {
  if (typeof window !== 'undefined' && window.location?.hostname) {
    const hostname = window.location.hostname;
    if (!isLocalOrPreviewHost(hostname)) {
      const parts = hostname.split('.');
      return `https://${parts.length > 2 ? parts.slice(1).join('.') : hostname}`;
    }
  }
  return BRAND.storefront.origin;
};

/** An event's sales page on the brand's storefront (MoMo: /events/<slug>; TKT storefronts: /<slug>). */
export const eventSalesPageUrl = (event) => (event?.slug || event?.id ? `${getStorefrontUrl()}${BRAND.storefront.eventPath(encodeURIComponent(event.slug || event.id))}` : null);
